import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry from 'react-masonry-css';
import CreateGuide from '@/app/assets/imgs/create_guide.svg';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { TemplateCard } from '@/app/components/shared/TemplateCard';
import { TemplateDetailDrawer } from '@/app/components/shared/TemplateDetailDrawer';
import { useDevice, useExpose, useTemplateDetail } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { Template } from './apis';
import { BREAK_POINT_COLUMNS } from './constants';

interface FeedProps {
  templates: Template[];
  hasMore: boolean;
  selectedTemplateCategory: string;
  next: (selectedTemplateCategory: string) => void;
  loading: boolean;
  isSearch: boolean;
}

export const Feed = ({ templates, hasMore, selectedTemplateCategory, next, loading, isSearch }: FeedProps) => {
  const { supportsHover } = useDevice();
  const router = useRouter();
  const t = useTranslations('templates');
  const templateCardRef = useRef<HTMLDivElement[]>([]);
  const { detail, openDetail, closeDetail, onPrev, onNext, hasPrev, hasNext } = useTemplateDetail({
    templates,
    hasMore,
    isLoading: loading,
    loadMore: () => next(selectedTemplateCategory),
    reportPosition: 'templates',
  });

  useExpose(
    (idx) => {
      const item = templates[idx];
      if (item) {
        reportEvent('templates_element_exposure', {
          element_name: `template_${item.scene}` || '',
          position: 'templates',
        });
      }
    },
    templateCardRef.current,
    templates,
  );

  const handleOpenTemplate = (scene: string, elementName: string) => {
    router.push(`/templates/${scene}?from=${selectedTemplateCategory}`);
    reportEvent('templates_element_click', {
      element_name: `${elementName}_${scene}`,
      position: 'templates',
    });
  };

  if (loading && !hasMore) {
    return (
      <div className="h-[calc(100vh-200px)]">
        <GlobalLoader />
      </div>
    );
  }

  return templates.length > 0 ? (
    <>
      <InfiniteScroll
        dataLength={templates.length}
        next={() => next(selectedTemplateCategory)}
        hasMore={hasMore}
        loader={<p style={{ textAlign: 'center' }}>Loading...</p>}
        scrollableTarget="scrollableDiv"
        className="mb-3"
      >
        <Masonry
          breakpointCols={BREAK_POINT_COLUMNS}
          className="-ml-3 flex w-auto"
          columnClassName="pl-3 bg-clip-padding"
        >
          {templates.map((item, index) => (
            <div
              ref={(el: HTMLDivElement) => {
                templateCardRef.current[index] = el;
              }}
              className="mb-3"
              key={item.id}
              data-index={index}
            >
              <TemplateCard
                variant="portrait"
                resource={item.creations?.find((creation) => creation.name === 'portrait')?.resource}
                title={item.scene_name || ''}
                tag={item.tag}
                showCreate
                onClick={() => {
                  supportsHover
                    ? handleOpenTemplate(item.scene, 'template')
                    : openDetail(item.scene, item.scene_name || '', index);
                }}
                onViewDetail={() => {
                  openDetail(item.scene, item.scene_name || '', index);
                }}
                isImageType={item.type === 'image'}
              />
            </div>
          ))}
        </Masonry>
      </InfiniteScroll>
      {/* 始终渲染 TemplateDetailDrawer，避免路由拦截时卸载导致数据丢失 */}
      <TemplateDetailDrawer
        open={!!detail?.scene}
        scene={detail?.scene || ''}
        title={detail?.title || ''}
        onClose={closeDetail}
        onPrev={onPrev}
        onNext={onNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onClickCreate={() => {
          handleOpenTemplate(detail?.scene || '', 'detail_create');
        }}
      />
    </>
  ) : (
    <div className="flex h-full flex-col items-center justify-center">
      <img
        src={CreateGuide.src}
        alt="create_guide"
        className="mb-8 w-[400px] opacity-60 sm:w-[200px] lg:mb-6 lg:w-[320px]"
      />
      <div className="text-center font-medium text-[18px] text-system-text03">
        {isSearch ? t('search.noResults') : t('noTemplates')}
      </div>
    </div>
  );
};
